import React, { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { IoCheckmarkDoneSharp } from "react-icons/io5";

const UpdateMeeting = ({ meetingIdData, onClose, fetchMeetings }) => {
    const [attendiesOptions, setAttendiesOptions] = useState([]);
    const [availabilityMessage, setAvailabilityMessage] = useState(null);
    const [availabilityStatus, setAvailabilityStatus] = useState(false);
    const [isSlotAvailable, setIsSlotAvailable] = useState(false);
    const [selectedDates, setSelectedDates] = useState({ slot_start_time: null, slot_end_time: null });
    const [initialValues, setInitialValues] = useState(null); // for pre-filled form data
    const meetingUrl = `${process.env.REACT_APP_BASE_URL}/meeting`;
    const availabilityUrl = `${process.env.REACT_APP_BASE_URL}/avl/meeting`;
   
    useEffect(() => {
        if (meetingIdData) {
            const fetchSingleMeeting = async () => {
                try {
                    const response = await fetch(`${meetingUrl}/${meetingIdData.id}`, {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem("token")}`,
                        },
                    });
                    const data = await response.json();
                    console.log(data)
                    if (data?.status) {
                        const meeting = data;
                        const start = new Date(meeting?.slot_start_time);
                        const end = new Date(meeting?.slot_end_time);
                        setSelectedDates({ slot_start_time: start, slot_end_time: end });
                        setInitialValues({
                            title: meeting?.title || "",
                            description: meeting?.description || "",
                            slot_start_time: start,
                            slot_end_time: end,
                            attendies: meeting?.attendies || [],
                        });
                        setAttendiesOptions(
                            (meeting?.attendies || []).map(user => ({
                                value: user?.user_id,
                                label: user?.user_name || user?.user_id,
                                ...user,
                                status: true
                            }))
                        );
                        setIsSlotAvailable(true); // assume current meeting slot is available
                        setAvailabilityStatus(true);
                    }
                } catch (error) {
                    console.error("Failed to fetch meeting data", error);
                }
            };

            fetchSingleMeeting();
        }
    }, [meetingIdData]);

    const checkAvailability = async (slot_start_time, slot_end_time) => {
        if (!slot_start_time || !slot_end_time) {
            setAvailabilityMessage("Please select start and end time.");
            return;
        }

        slot_start_time = format(new Date(slot_start_time), "yyyy-MM-dd HH:mm:ss");
        slot_end_time = format(new Date(slot_end_time), "yyyy-MM-dd HH:mm:ss");

        try {
            const response = await fetch(`${availabilityUrl}?slot_start_time=${encodeURIComponent(slot_start_time)}&slot_end_time=${encodeURIComponent(slot_end_time)}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
            });
            const data = await response.json();
            const formattedUsers = (data?.data || [])
                .sort((a, b) => b.status - a.status)
                .map(user => ({
                    value: user.user_id,
                    label: user.user_name || user.user_id,
                    ...user
                }));

            if (data?.status === true && data?.msg === "0") {
                Swal.fire({ title: "Slot Available", icon: "success" });
                setIsSlotAvailable(true);
            } else if (data?.status === true && data?.msg === "1") {
                Swal.fire({
                    title: data.message,
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonText: "Yes",
                    cancelButtonText: "No"
                }).then((result) => {
                    if (result.dismiss === Swal.DismissReason.cancel) onClose();
                });
            } else {
                Swal.fire({ title: data?.message || "Slot Not Available", icon: "error" });
                setIsSlotAvailable(false);
            }

            setAttendiesOptions(formattedUsers);
            setAvailabilityStatus(data.msg);
        } catch (error) {
            console.error("API Call Error:", error);
            setIsSlotAvailable(false);
        }
    };

    useEffect(() => {
        setIsSlotAvailable(false);
        setAvailabilityMessage(null);
    }, [selectedDates.slot_start_time, selectedDates.slot_end_time]);


    const validationSchema = Yup.object().shape({
        title: Yup.string().required("Title is required"),
        description: Yup.string().required("Description is required"),
        slot_start_time: Yup.string().required("Start time is required"),
        slot_end_time: Yup.string().required("End time is required"),
        attendies: Yup.array().min(1, "At least one attendee is required"),
    });

    if (!initialValues) return <div className="loading-text">Loading meeting...</div>;

    return (
        <div className="modal-overlay">
            <div className="modal-container animate-modal">
                <h2 className="modal-title">✏️ Update Meeting</h2>
                <Formik
                    enableReinitialize
                    initialValues={{
                        title: meetingIdData?.title || "",
                        description: meetingIdData?.description || "",
                        slot_start_time: meetingIdData?.slot_start_time ? new Date(meetingIdData.slot_start_time) : null,
                        slot_end_time: meetingIdData?.slot_end_time ? new Date(meetingIdData.slot_end_time) : null,
                        attendies: meetingIdData?.attendies?.map(att => ({
                            user_id: att.user_id,
                            user_name: att.user_name,
                            designation: att.designation
                        })) || [],
                    }}
                    validationSchema={validationSchema}
                    onSubmit={(values, { setSubmitting, resetForm }) => {
                        const token = localStorage.getItem("token");

                        const formattedValues = {
                            ...values,
                            slot_start_time: format(new Date(values.slot_start_time), "yyyy-MM-dd HH:mm:ss"),
                            slot_end_time: format(new Date(values.slot_end_time), "yyyy-MM-dd HH:mm:ss"),
                            attendies: values.attendies
                        };
                        // console.log(formattedValues)
                        fetch(`${meetingUrl}/${meetingIdData.id}`, {
                            method: "PUT",
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${token}`,
                            },
                            body: JSON.stringify(formattedValues),
                        }).then(response => response.json())
                            .then(data => {
                                if (data.status) {
                                    Swal.fire({
                                        title: "Meeting Updated!",
                                        text: data.message || "Your meeting has been successfully updated.",
                                        icon: "success",
                                        confirmButtonText: "OK",
                                    });
                                    resetForm();
                                    onClose();
                                    fetchMeetings();
                                } else {
                                    Swal.fire({
                                        title: "Update Failed",
                                        text: data.message || "Something went wrong while updating the meeting.",
                                        icon: "error",
                                        confirmButtonText: "OK",
                                    });
                                }
                            })
                            .catch(error => {
                                console.error("Error updating meeting:", error);
                                Swal.fire({
                                    title: "Error",
                                    text: "Failed to update the meeting. Please try again.",
                                    confirmButtonText: "OK",
                                });
                            })
                            .finally(() => {
                                setSubmitting(false);
                            });
                    }}
                >
                    {({ handleSubmit, setFieldValue, values }) => (
                        <Form onSubmit={handleSubmit}>
                            <div className="date-time-container">
                                <div>
                                    <DatePicker
                                        selected={values.slot_start_time}
                                        onChange={(date) => {
                                            setFieldValue("slot_start_time", date);
                                            setSelectedDates((prev) => ({ ...prev, slot_start_time: date }));
                                            setFieldValue("attendies", []);
                                        }}
                                        showTimeSelect
                                        dateFormat="Pp"
                                        className="input-field"
                                        placeholderText="Start Time"
                                        minDate={new Date()}
                                    />
                                    <ErrorMessage name="slot_start_time" component="div" className="error-message" />
                                </div>
                                <span className="arrow">➡</span>
                                <div>
                                    <DatePicker
                                        selected={values.slot_end_time}
                                        onChange={(date) => {
                                            setFieldValue("slot_end_time", date);
                                            setSelectedDates((prev) => ({ ...prev, slot_end_time: date }));
                                            setFieldValue("attendies", []);
                                        }}
                                        showTimeSelect
                                        dateFormat="Pp"
                                        className="input-field"
                                        placeholderText="End Time"
                                        minDate={values.slot_start_time || new Date()}
                                    />
                                    <ErrorMessage name="slot_end_time" component="div" className="error-message" />
                                </div>
                                <button
                                    type="button"
                                    className="search-button-time-container"
                                    onClick={() => checkAvailability(values.slot_start_time, values.slot_end_time)}
                                    disabled={
                                        !(selectedDates.slot_start_time && selectedDates.slot_end_time) ||
                                        isSlotAvailable || values.attendies.length > 0
                                    }
                                    style={{
                                        backgroundColor: !(selectedDates.slot_start_time && selectedDates.slot_end_time) ||
                                            isSlotAvailable || values.attendies.length > 0 ? "gray" : "#04a5e8",
                                        color: "white",
                                        cursor: !(selectedDates.slot_start_time && selectedDates.slot_end_time) ||
                                            isSlotAvailable || values.attendies.length > 0 ? "not-allowed" : "pointer",
                                        opacity: !(selectedDates.slot_start_time && selectedDates.slot_end_time) ||
                                            isSlotAvailable || values.attendies.length > 0 ? 0.6 : 1,
                                    }}
                                >
                                    {isSlotAvailable ? <IoCheckmarkDoneSharp size={20} /> : "Check"}
                                </button>
                            </div>

                            {availabilityMessage && <div className="availability-message">{availabilityMessage}</div>}

                            <Field type="text" name="title" className="input-field" placeholder="📌 Add title" />
                            <ErrorMessage name="title" component="div" className="error-message" />

                            {availabilityStatus && (
                                <Select
                                    name="attendies"
                                    className="input-field custom-select"
                                    options={attendiesOptions}
                                    isMulti
                                    value={attendiesOptions.filter(option =>
                                        values.attendies.some(att => att.user_id === option.value)
                                    )}
                                    onChange={selectedOptions =>
                                        setFieldValue("attendies", selectedOptions?.map(option => ({
                                            user_id: option.value,
                                            user_name: option.label,
                                            designation: option.designation
                                        })) || [])
                                    }
                                    getOptionLabel={(e) => (
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                maxWidth: "100%",
                                                color: e.status ? "black" : "darkred",
                                                fontWeight: "600",
                                                opacity: e.status ? 1 : 0.5,
                                                cursor: e.status ? "pointer" : "not-allowed",
                                                textTransform: "capitalize"
                                            }}
                                            title={!e.status ? "Already booked" : ""}
                                        >
                                            {e.label}
                                        </div>
                                    )}
                                    isOptionDisabled={(option) => !option.status}
                                    styles={{
                                        control: (provided) => ({
                                            ...provided,
                                            minHeight: "30px",
                                            fontSize: "14px",
                                        }),
                                        dropdownIndicator: (provided) => ({
                                            ...provided,
                                            padding: "4px"
                                        }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            padding: "5px 10px",
                                            fontSize: "14px",
                                            backgroundColor: state.isFocused ? "#f0f0f0" : "white",
                                            color: "#333",
                                            whiteSpace: "nowrap",
                                            overflowY: "auto",
                                            textOverflow: "ellipsis",
                                            maxWidth: "100%"
                                        }),
                                        menu: (provided) => ({
                                            ...provided,
                                            width: "200px",
                                            overflowY: "auto"
                                        })
                                    }}
                                />
                            )}

                            <Field as="textarea" name="description" className="textarea-field" placeholder="📝 Meeting description" />
                            <ErrorMessage name="description" component="div" className="error-message" />

                            <div className="button-group">
                                <button type="button" className="close-button" onClick={onClose}>Close</button>
                                <button type="submit" className="save-button">Save</button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};

export default UpdateMeeting;
